/**
 * 鷹架工具執行器
 * 負責生成互動式引導表單
 */

/** 鷹架類型對應的節點標籤（用於創建帶特殊標籤的節點） */
export const SCAFFOLD_TAGS = {
  summarize: 'summary',
  compare: 'compare',
  question: 'question',
  insight: 'insight',
  expand: 'expand',
};

/**
 * 生成「下一步：新增為節點」區塊 HTML（含按鈕，點擊後觸發創建節點並帶對應標籤）
 * @param {string} storageKey - 表單 localStorage key 前綴
 * @param {string} toolId - 工具 id (summarize / compare / question / insight / expand)
 * @param {string} scaffoldTag - 節點標籤 (summary / compare / question / insight / expand)
 * @param {string} buttonLabel - 按鈕文字，如「新增為節點（摘要）」
 */
const makeNextStepBlock = (storageKey, toolId, scaffoldTag, buttonLabel) => {
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  return `
    <div style="background: #e8f5e9; padding: 16px; border-radius: 8px; border-left: 4px solid #4caf50; text-align: center;">
      <p style="margin: 0 0 12px 0; font-size: 1rem; font-weight: 600; color: #2e7d32;">
        ✍️ 下一步：分享你的內容
      </p>
      <p style="margin: 0 0 12px 0; font-size: 0.9rem; color: #424242; line-height: 1.5;">
        完成填寫後，點擊下方按鈕即可<strong>直接建立節點</strong>並帶有「${buttonLabel}」標籤，<br/>
        讓其他人也能看到你的整理與見解！
      </p>
      <button type="button" class="scaffold-create-node-btn" data-storage-key="${esc(storageKey)}" data-tool-id="${esc(toolId)}" data-scaffold-tag="${esc(scaffoldTag)}" data-default-title="${esc(buttonLabel)}" style="margin-top: 12px; padding: 10px 20px; font-size: 0.95rem; font-weight: 600; color: #fff; background: #4caf50; border: none; border-radius: 8px; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
        ➕ ${buttonLabel}
      </button>
      <p style="margin: 12px 0 0 0; font-size: 0.85rem; color: #666;">
        💡 也可在工作區左下選擇新增想法，或點擊節點進行回覆
      </p>
    </div>
  `;
};

/**
 * 生成節點列表 HTML
 */
const generateNodeList = (nodeContents) => {
  return nodeContents.map((n, i) => 
    `<li style="margin-bottom: 8px;">
      <strong style="color: #1976d2;">${n.title || `節點 ${i + 1}`}</strong><br/>
      <span style="color: #666; font-size: 0.9rem;">${n.content?.substring(0, 150) || '(無內容)'}${n.content?.length > 150 ? '...' : ''}</span><br/>
      <em style="color: #999; font-size: 0.85rem;">— ${n.author || '未知作者'}</em>
    </li>`
  ).join('');
};

/**
 * 摘要鷹架工具
 */
export const executeSummarize = (nodeContents) => {
  const nodeList = generateNodeList(nodeContents);
  // 使用節點ID生成穩定的 key，讓相同節點組合可以恢復資料
  const nodeIds = nodeContents.map(node => node.id || node.title).join('-');
  const storageKey = `scaffold-summarize-${nodeIds}`;
  
  return `
    <div style="padding: 16px; font-family: system-ui, -apple-system, sans-serif;" data-storage-key="${storageKey}">
      <h4 style="color: #1976d2; margin-bottom: 16px;">📝 摘要鷹架</h4>
      
      <div style="background: #f5f5f5; padding: 12px; border-radius: 8px; margin-bottom: 16px;">
        <h5 style="color: #424242; margin: 0 0 8px 0; font-size: 0.9rem;">已選擇的節點：</h5>
        <ul style="list-style: none; padding: 0; margin: 0;">${nodeList}</ul>
      </div>

      <div style="background: #e3f2fd; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
        <h5 style="color: #1565c0; margin: 0 0 12px 0;">💭 引導提問</h5>
        
        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            1. 這${nodeContents.length}個想法的核心主題是什麼？
          </label>
          <textarea 
            id="${storageKey}-q1"
            style="width: 100%; min-height: 80px; padding: 10px; border: 2px solid #bbdefb; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="在此輸入你的想法..."
            oninput="localStorage.setItem('${storageKey}-q1', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q1')||'';this.dataset.loaded='1'}"></textarea>
        </div>

        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            2. 如何用一句話總結這些觀點？
          </label>
          <textarea 
            id="${storageKey}-q2"
            style="width: 100%; min-height: 60px; padding: 10px; border: 2px solid #bbdefb; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="試著濃縮成一句話..."
            oninput="localStorage.setItem('${storageKey}-q2', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q2')||'';this.dataset.loaded='1'}"></textarea>
        </div>

        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            3. 這些內容的關鍵要點有哪些？
          </label>
          <textarea 
            id="${storageKey}-q3"
            style="width: 100%; min-height: 100px; padding: 10px; border: 2px solid #bbdefb; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="列出 3-5 個關鍵要點..."
            oninput="localStorage.setItem('${storageKey}-q3', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q3')||'';this.dataset.loaded='1'}"></textarea>
        </div>
      </div>
      
      <script>
        (function() {
          const key = '${storageKey}';
          ['q1', 'q2', 'q3'].forEach(q => {
            const el = document.getElementById(key + '-' + q);
            if (el) {
              const saved = localStorage.getItem(key + '-' + q);
              if (saved) el.value = saved;
            }
          });
        })();
      </script>

      <div style="background: #fff3e0; padding: 12px; border-radius: 8px; border-left: 4px solid #ff9800; margin-bottom: 16px;">
        <p style="margin: 0; font-size: 0.9rem; color: #e65100;"><strong>💡 建議步驟：</strong></p>
        <ol style="margin: 8px 0 0 0; padding-left: 20px; font-size: 0.9rem; color: #424242;">
          <li>找出共同點和差異</li>
          <li>提煉關鍵概念</li>
          <li>組織成連貫的摘要</li>
        </ol>
      </div>

      ${makeNextStepBlock(storageKey, 'summarize', SCAFFOLD_TAGS.summarize, '新增為節點（摘要）')}
    </div>
  `;
};

/**
 * 比較鷹架工具
 */
export const executeCompare = (nodeContents) => {
  const nodeList = generateNodeList(nodeContents);
  // 使用節點ID生成穩定的 key，讓相同節點組合可以恢復資料
  const nodeIds = nodeContents.map(node => node.id || node.title).join('-');
  const storageKey = `scaffold-compare-${nodeIds}`;
  
  return `
    <div style="padding: 16px; font-family: system-ui, -apple-system, sans-serif;" data-storage-key="${storageKey}">
      <h4 style="color: #1976d2; margin-bottom: 16px;">⚖️ 比較鷹架</h4>
      
      <div style="background: #f5f5f5; padding: 12px; border-radius: 8px; margin-bottom: 16px;">
        <h5 style="color: #424242; margin: 0 0 8px 0; font-size: 0.9rem;">要比較的節點：</h5>
        <ul style="list-style: none; padding: 0; margin: 0;">${nodeList}</ul>
      </div>

      <div style="background: #f3e5f5; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
        <h5 style="color: #6a1b9a; margin: 0 0 12px 0;">💭 比較維度</h5>
        
        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            相似之處
          </label>
          <textarea 
            id="${storageKey}-q1"
            style="width: 100%; min-height: 80px; padding: 10px; border: 2px solid #e1bee7; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="這些想法在哪些方面相似？共同強調什麼？"
            oninput="localStorage.setItem('${storageKey}-q1', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q1')||'';this.dataset.loaded='1'}"></textarea>
        </div>

        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            差異之處
          </label>
          <textarea 
            id="${storageKey}-q2"
            style="width: 100%; min-height: 80px; padding: 10px; border: 2px solid #e1bee7; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="在哪些方面不同？立場或角度有何差異？"
            oninput="localStorage.setItem('${storageKey}-q2', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q2')||'';this.dataset.loaded='1'}"></textarea>
        </div>

        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            觀點立場分析
          </label>
          <textarea 
            id="${storageKey}-q3"
            style="width: 100%; min-height: 80px; padding: 10px; border: 2px solid #e1bee7; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="哪些觀點相互支持？哪些存在矛盾或衝突？"
            oninput="localStorage.setItem('${storageKey}-q3', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q3')||'';this.dataset.loaded='1'}"></textarea>
        </div>
      </div>

      <div style="background: #e8f5e9; padding: 12px; border-radius: 8px; border-left: 4px solid #4caf50; margin-bottom: 16px;">
        <p style="margin: 0; font-size: 0.9rem; color: #2e7d32;"><strong>📊 其他可比較的維度：</strong></p>
        <ul style="margin: 8px 0 0 0; padding-left: 20px; font-size: 0.85rem; color: #424242;">
          <li>論證方式：理論 vs. 實例 vs. 數據</li>
          <li>深度廣度：具體細節 vs. 宏觀視角</li>
          <li>創新程度：傳統想法 vs. 新穎觀點</li>
        </ul>
      </div>

      ${makeNextStepBlock(storageKey, 'compare', SCAFFOLD_TAGS.compare, '新增為節點（比較）')}
    </div>
  `;
};

/**
 * 提問鷹架工具
 */
export const executeQuestion = (nodeContents) => {
  const nodeList = generateNodeList(nodeContents);
  // 使用節點ID生成穩定的 key，讓相同節點組合可以恢復資料
  const nodeIds = nodeContents.map(node => node.id || node.title).join('-');
  const storageKey = `scaffold-question-${nodeIds}`;
  
  return `
    <div style="padding: 16px; font-family: system-ui, -apple-system, sans-serif;" data-storage-key="${storageKey}">
      <h4 style="color: #1976d2; margin-bottom: 16px;">❓ 提問鷹架</h4>
      
      <div style="background: #f5f5f5; padding: 12px; border-radius: 8px; margin-bottom: 16px;">
        <h5 style="color: #424242; margin: 0 0 8px 0; font-size: 0.9rem;">基於以下節點發展問題：</h5>
        <ul style="list-style: none; padding: 0; margin: 0;">${nodeList}</ul>
      </div>

      <div style="background: #e8f5e9; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
        <h5 style="color: #2e7d32; margin: 0 0 12px 0;">💭 發展你的問題</h5>
        
        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            澄清型問題 <span style="font-weight: 400; color: #666; font-size: 0.85rem;">（釐清意義和定義）</span>
          </label>
          <textarea 
            id="${storageKey}-q1"
            style="width: 100%; min-height: 70px; padding: 10px; border: 2px solid #c8e6c9; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="例如：「這是什麼意思？」「為什麼會這樣？」"
            oninput="localStorage.setItem('${storageKey}-q1', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q1')||'';this.dataset.loaded='1'}"></textarea>
        </div>

        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            挑戰型問題 <span style="font-weight: 400; color: #666; font-size: 0.85rem;">（質疑和批判）</span>
          </label>
          <textarea 
            id="${storageKey}-q2"
            style="width: 100%; min-height: 70px; padding: 10px; border: 2px solid #c8e6c9; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="例如：「這個觀點的限制是什麼？」「有沒有反例？」"
            oninput="localStorage.setItem('${storageKey}-q2', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q2')||'';this.dataset.loaded='1'}"></textarea>
        </div>

        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            延伸型問題 <span style="font-weight: 400; color: #666; font-size: 0.85rem;">（拓展思考）</span>
          </label>
          <textarea 
            id="${storageKey}-q3"
            style="width: 100%; min-height: 70px; padding: 10px; border: 2px solid #c8e6c9; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="例如：「如果...會怎樣？」「還可以探索什麼？」"
            oninput="localStorage.setItem('${storageKey}-q3', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q3')||'';this.dataset.loaded='1'}"></textarea>
        </div>

        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            連結型問題 <span style="font-weight: 400; color: #666; font-size: 0.85rem;">（建立關聯）</span>
          </label>
          <textarea 
            id="${storageKey}-q4"
            style="width: 100%; min-height: 70px; padding: 10px; border: 2px solid #c8e6c9; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="例如：「這與...有什麼關係？」「如何連結其他概念？」"
            oninput="localStorage.setItem('${storageKey}-q4', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q4')||'';this.dataset.loaded='1'}"></textarea>
        </div>
      </div>

      <div style="background: #fff9c4; padding: 12px; border-radius: 8px; border-left: 4px solid #fbc02d; margin-bottom: 16px;">
        <p style="margin: 0; font-size: 0.85rem; color: #f57f17;">
          💡 <strong>提示：</strong>好問題能引導更深入的思考，試著從不同角度提問！
        </p>
      </div>

      ${makeNextStepBlock(storageKey, 'question', SCAFFOLD_TAGS.question, '新增為節點（提問）')}
    </div>
  `;
};

/**
 * 洞察鷹架工具
 */
export const executeInsight = (nodeContents) => {
  const nodeList = generateNodeList(nodeContents);
  // 使用節點ID生成穩定的 key，讓相同節點組合可以恢復資料
  const nodeIds = nodeContents.map(node => node.id || node.title).join('-');
  const storageKey = `scaffold-insight-${nodeIds}`;
  
  return `
    <div style="padding: 16px; font-family: system-ui, -apple-system, sans-serif;" data-storage-key="${storageKey}">
      <h4 style="color: #1976d2; margin-bottom: 16px;">💡 洞察鷹架</h4>
      
      <div style="background: #f5f5f5; padding: 12px; border-radius: 8px; margin-bottom: 16px;">
        <h5 style="color: #424242; margin: 0 0 8px 0; font-size: 0.9rem;">分析以下節點：</h5>
        <ul style="list-style: none; padding: 0; margin: 0;">${nodeList}</ul>
      </div>

      <div style="background: #fff9c4; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
        <h5 style="color: #f57f17; margin: 0 0 12px 0;">💭 深度思考</h5>
        
        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            隱含的深層道理
          </label>
          <textarea 
            id="${storageKey}-q1"
            style="width: 100%; min-height: 80px; padding: 10px; border: 2px solid #fff59d; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="這些想法背後隱含了什麼更深層的道理或原理？"
            oninput="localStorage.setItem('${storageKey}-q1', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q1')||'';this.dataset.loaded='1'}"></textarea>
        </div>

        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            模式與趨勢
          </label>
          <textarea 
            id="${storageKey}-q2"
            style="width: 100%; min-height: 80px; padding: 10px; border: 2px solid #fff59d; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="你看到了什麼模式、規律或趨勢？"
            oninput="localStorage.setItem('${storageKey}-q2', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q2')||'';this.dataset.loaded='1'}"></textarea>
        </div>

        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            意外的發現
          </label>
          <textarea 
            id="${storageKey}-q3"
            style="width: 100%; min-height: 80px; padding: 10px; border: 2px solid #fff59d; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="有什麼是作者沒有明說，但你能察覺到的？有什麼出乎意料的連結？"
            oninput="localStorage.setItem('${storageKey}-q3', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q3')||'';this.dataset.loaded='1'}"></textarea>
        </div>
      </div>

      <div style="background: #e1f5fe; padding: 12px; border-radius: 8px; border-left: 4px solid #0288d1; margin-bottom: 16px;">
        <p style="margin: 0 0 8px 0; font-size: 0.9rem; color: #01579b;"><strong>🔍 洞察提示：</strong></p>
        <ul style="margin: 0; padding-left: 20px; font-size: 0.85rem; color: #424242;">
          <li>這些觀點基於什麼假設或前提？</li>
          <li>反映了什麼價值觀或立場？</li>
          <li>各部分如何相互影響或連結？</li>
        </ul>
      </div>

      ${makeNextStepBlock(storageKey, 'insight', SCAFFOLD_TAGS.insight, '新增為節點（洞察）')}
    </div>
  `;
};

/**
 * 延伸鷹架工具
 */
export const executeExpand = (nodeContents) => {
  const nodeList = generateNodeList(nodeContents);
  // 使用節點ID生成穩定的 key，讓相同節點組合可以恢復資料
  const nodeIds = nodeContents.map(node => node.id || node.title).join('-');
  const storageKey = `scaffold-expand-${nodeIds}`;
  
  return `
    <div style="padding: 16px; font-family: system-ui, -apple-system, sans-serif;" data-storage-key="${storageKey}">
      <h4 style="color: #1976d2; margin-bottom: 16px;">🚀 延伸鷹架</h4>
      
      <div style="background: #f5f5f5; padding: 12px; border-radius: 8px; margin-bottom: 16px;">
        <h5 style="color: #424242; margin: 0 0 8px 0; font-size: 0.9rem;">延伸以下節點：</h5>
        <ul style="list-style: none; padding: 0; margin: 0;">${nodeList}</ul>
      </div>

      <div style="background: #fce4ec; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
        <h5 style="color: #c2185b; margin: 0 0 12px 0;">💭 延伸方向</h5>
        
        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            廣度延伸 <span style="font-weight: 400; color: #666; font-size: 0.85rem;">（相關領域、跨學科連結）</span>
          </label>
          <textarea 
            id="${storageKey}-q1"
            style="width: 100%; min-height: 75px; padding: 10px; border: 2px solid #f8bbd0; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="這些想法可以連結到哪些其他領域或議題？"
            oninput="localStorage.setItem('${storageKey}-q1', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q1')||'';this.dataset.loaded='1'}"></textarea>
        </div>

        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            深度延伸 <span style="font-weight: 400; color: #666; font-size: 0.85rem;">（細節分析、機制探討）</span>
          </label>
          <textarea 
            id="${storageKey}-q2"
            style="width: 100%; min-height: 75px; padding: 10px; border: 2px solid #f8bbd0; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="可以進一步深入探討哪些細節或運作機制？"
            oninput="localStorage.setItem('${storageKey}-q2', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q2')||'';this.dataset.loaded='1'}"></textarea>
        </div>

        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            實踐延伸 <span style="font-weight: 400; color: #666; font-size: 0.85rem;">（應用場景、解決方案）</span>
          </label>
          <textarea 
            id="${storageKey}-q3"
            style="width: 100%; min-height: 75px; padding: 10px; border: 2px solid #f8bbd0; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="如何將這些想法應用到實際情境？可以解決什麼問題？"
            oninput="localStorage.setItem('${storageKey}-q3', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q3')||'';this.dataset.loaded='1'}"></textarea>
        </div>

        <div style="margin-bottom: 16px;">
          <label style="display: block; font-weight: 600; margin-bottom: 6px; color: #424242;">
            批判延伸 <span style="font-weight: 400; color: #666; font-size: 0.85rem;">（反思限制、提出改進）</span>
          </label>
          <textarea 
            id="${storageKey}-q4"
            style="width: 100%; min-height: 75px; padding: 10px; border: 2px solid #f8bbd0; border-radius: 6px; font-size: 0.9rem; font-family: inherit; resize: vertical;"
            placeholder="這些想法有什麼限制？如何改進或補充？"
            oninput="localStorage.setItem('${storageKey}-q4', this.value)"
            onfocus="if(!this.dataset.loaded){this.value=localStorage.getItem('${storageKey}-q4')||'';this.dataset.loaded='1'}"></textarea>
        </div>
      </div>

      <div style="background: #e0f2f1; padding: 12px; border-radius: 8px; border-left: 4px solid #00897b; margin-bottom: 16px;">
        <p style="margin: 0; font-size: 0.85rem; color: #004d40;">
          💡 <strong>提示：</strong>延伸思考幫助你從現有想法出發，探索更多可能性！
        </p>
      </div>

      ${makeNextStepBlock(storageKey, 'expand', SCAFFOLD_TAGS.expand, '新增為節點（延伸）')}
    </div>
  `;
};

/**
 * 鷹架工具執行路由
 */
export const executeScaffoldTool = (toolId, nodeContents) => {
  switch (toolId) {
    case 'summarize':
      return executeSummarize(nodeContents);
    case 'compare':
      return executeCompare(nodeContents);
    case 'question':
      return executeQuestion(nodeContents);
    case 'insight':
      return executeInsight(nodeContents);
    case 'expand':
      return executeExpand(nodeContents);
    default:
      return `<p style="color: red;">未知的工具: ${toolId}</p>`;
  }
};
